import { Box, IconButton, InputAdornment } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo, useRef } from "react"
import { useFormContext } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { FormNumericField, FormSelect, FormTextField } from "@/components/UiKit"
import { WarningMessage } from "@/components/WarningMessage/WarningMessage"
import { type Product, ProductPackagingType, type UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { useHasExistingProductTest } from "@/hooks/useHasExistingProductTest"
import { runWithGuardRef } from "@/utils/forms"
import { roundToPrecision } from "@/utils/number"
import { getProductPackagingTypeLabel } from "@/utils/product"
import useDebounce from "@/utils/useDebounce"

enum LotFormField {
  QuantityPerPackage = "QUANTITY_PER_PACKAGE",
  UnitsCount = "UNITS_COUNT",
  Quantity = "QUANTITY",
}

interface SoftReceivedLotFormProps {
  baseFieldPath: string
  product: Product
  uom: UnitOfMeasurement | undefined
  isLast: boolean
  canMaintain: boolean
  isResettingRef?: React.MutableRefObject<boolean>
  onAdd: () => void
  onRemove: () => void
}

export const SoftReceivedLotForm: FC<SoftReceivedLotFormProps> = ({
  baseFieldPath,
  product,
  uom,
  isLast,
  canMaintain,
  isResettingRef,
  onAdd,
  onRemove,
}) => {
  const { control, watch, setValue } = useFormContext()
  const isUpdatingRef = useRef<LotFormField | null>(null)

  const fieldPaths = {
    lotNumber: `${baseFieldPath}.lotNumber` as const,
    batchNumber: `${baseFieldPath}.batchNumber` as const,
    packageUnitQuantity: `${baseFieldPath}.packageUnitQuantity` as const,
    packagingType: `${baseFieldPath}.packagingType` as const,
    unitsCount: `${baseFieldPath}.unitsCount` as const,
    quantity: `${baseFieldPath}.quantity` as const,
  }

  const lotNumber = watch(fieldPaths.lotNumber)
  const batchNumber = watch(fieldPaths.batchNumber)
  const packageUnitQuantity = watch(fieldPaths.packageUnitQuantity)
  const unitsCount = watch(fieldPaths.unitsCount)

  const productTestFilter = useMemo(
    () => ({
      productId: product.productId,
      lotNumber,
      batchNumber,
    }),
    [product.productId, lotNumber, batchNumber]
  )
  const debouncedProductTestFilter = useDebounce(productTestFilter, 300)
  const { hasExisting: hasExistingProductTest, isFetching: isFetchingProductTests } = useHasExistingProductTest({
    filter: debouncedProductTestFilter,
  })

  const isProductTestFilterChanged =
    productTestFilter.lotNumber !== debouncedProductTestFilter.lotNumber ||
    productTestFilter.batchNumber !== debouncedProductTestFilter.batchNumber

  const shouldShowLotNumberWarning =
    lotNumber && !isProductTestFilterChanged && !isFetchingProductTests && !hasExistingProductTest

  const packagingTypeOptions = Object.values(ProductPackagingType).map((packagingType) => ({
    value: packagingType,
    label: getProductPackagingTypeLabel(packagingType),
  }))

  const handleQuantityPerPackageChange = (value: number | null) => {
    if (
      isResettingRef?.current ||
      (isUpdatingRef.current !== null && isUpdatingRef.current !== LotFormField.QuantityPerPackage)
    ) {
      return
    }

    runWithGuardRef(
      () => {
        const quantity = unitsCount === null || value === null ? null : roundToPrecision(unitsCount * value, 2)
        setValue(fieldPaths.quantity, quantity, { shouldValidate: true })
      },
      isUpdatingRef,
      {
        activeValue: LotFormField.QuantityPerPackage,
        inactiveValue: null,
      }
    )
  }

  const handleUnitsCountChange = (value: number | null) => {
    if (
      isResettingRef?.current ||
      (isUpdatingRef.current !== null && isUpdatingRef.current !== LotFormField.UnitsCount)
    ) {
      return
    }

    runWithGuardRef(
      () => {
        const quantity =
          value === null || packageUnitQuantity === null ? null : roundToPrecision(value * packageUnitQuantity, 2)
        setValue(fieldPaths.quantity, quantity, { shouldValidate: true })
      },
      isUpdatingRef,
      {
        activeValue: LotFormField.UnitsCount,
        inactiveValue: null,
      }
    )
  }

  const handleQuantityChange = (value: number | null) => {
    if (
      isResettingRef?.current ||
      (isUpdatingRef.current !== null && isUpdatingRef.current !== LotFormField.Quantity)
    ) {
      return
    }

    runWithGuardRef(
      () => {
        let unitsCount: number | null

        if (value === null || packageUnitQuantity === null) {
          unitsCount = null
        } else {
          unitsCount = packageUnitQuantity === 0 ? 0 : roundToPrecision(value / packageUnitQuantity, 2)
        }

        setValue(fieldPaths.unitsCount, unitsCount, { shouldValidate: true })
      },
      isUpdatingRef,
      {
        activeValue: LotFormField.Quantity,
        inactiveValue: null,
      }
    )
  }

  return (
    <Box className='flex items-start gap-x-4' data-testid='lot-form'>
      <Box className='flex flex-col gap-y-2' style={{ flex: "2 2 0" }}>
        <Box className='flex gap-x-4'>
          {isLast && canMaintain ? (
            <Box className='flex flex-1'>
              <FormTextField
                control={control}
                name={fieldPaths.lotNumber}
                label='Lot #'
                required
                disabled={!canMaintain}
                className='flex-1'
                slotProps={{ input: { sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 } } }}
                data-testid='lot-number-field'
              />
              {canMaintain && (
                <Button
                  color='primary'
                  className='mt-[22px] rounded-l-none rounded-r px-1'
                  onClick={onAdd}
                  data-testid='add-lot-button'
                >
                  <AddOutlined />
                </Button>
              )}
            </Box>
          ) : (
            <FormTextField
              control={control}
              name={fieldPaths.lotNumber}
              label='Lot #'
              required
              disabled={!canMaintain}
              className='flex-1'
              data-testid='lot-number-field'
            />
          )}
          <FormTextField
            control={control}
            name={fieldPaths.batchNumber}
            label='Batch #'
            disabled={!canMaintain}
            className='flex-1'
            data-testid='batch-number-field'
          />
        </Box>
        {shouldShowLotNumberWarning && (
          <WarningMessage message={batchNumber ? "Invalid Lot # and Batch #" : "Invalid Lot #"} />
        )}
      </Box>
      <FormNumericField
        control={control}
        name={fieldPaths.packageUnitQuantity}
        label='Package UoM'
        required
        allowNegative={false}
        decimalScale={2}
        disabled={!canMaintain}
        className='flex-1'
        slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uom?.name}</InputAdornment> } }}
        onChange={handleQuantityPerPackageChange}
        data-testid='quantity-per-package-field'
      />
      <FormSelect
        control={control}
        name={fieldPaths.packagingType}
        label='Packaging'
        required
        options={packagingTypeOptions}
        disabled={!canMaintain}
        className='flex-1'
        data-testid='package-type-field'
      />
      <FormNumericField
        control={control}
        name={fieldPaths.unitsCount}
        label='Units per Lot'
        required
        allowNegative={false}
        decimalScale={2}
        disabled={!canMaintain}
        className='flex-1'
        onChange={handleUnitsCountChange}
        data-testid='units-count-field'
      />
      <FormNumericField
        control={control}
        name={fieldPaths.quantity}
        label='Total UoM per Lot'
        required
        allowNegative={false}
        decimalScale={2}
        disabled={!canMaintain}
        className='flex-1'
        slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uom?.name}</InputAdornment> } }}
        onChange={handleQuantityChange}
        data-testid='total-uom-field'
      />
      {canMaintain && (
        <IconButton onClick={onRemove} data-testid='remove-lot-button' className='mt-[22px]'>
          <DeleteOutlined className='text-gray-600' />
        </IconButton>
      )}
    </Box>
  )
}
